//=================================================
// Guides
//=================================================
// Pages are server-rendered from their URL params (language, type, stop, stop_item, open).
// Language changes are Turbo visits; stop/tab/type selection instantly toggles pre-rendered
// content and syncs the URL. The audio player is data-turbo-permanent so playback survives visits.

import { queryParameters, setFocus } from './utilities';
import { pauseYT, setupVideos } from './videos';
import { adjustLinkRails } from './navigation';
import { setupScrollGradients } from './exhibitions';

const GUIDE_PATH_PATTERN = /^\/guides(\/[^/]+)?$/;

let globalEventsBound = false;

// Applied stop item and last-reported stop, reset on each page view
let lastAppliedItemId = null;
let lastTrackedStopId = null;

function safelyParseJSON(str) {
    try {
        return JSON.parse(str);
    } catch (ex) {
        return null;
    }
}

// Get stop type but fallback to default if the value is bad
function getType(params) {
    return ['all', 'kids', 'access'].includes(params.type) ? params.type : 'all';
}

function guideShowPage() {
    const page = document.querySelector('.guide-page');

    return page && !page.hasAttribute('data-guide-list') ? page : null;
}

// Push guide event to data layer
// params: { guide_name, label, language, type, value }
export function pushGuideEvent(params = {}) {
    if (!useAnalytics) return;

    const { language, type } = queryParameters();
    // Overrides win over URL values (the URL may not reflect recent changes yet)
    const values = {
        guide_name: document.querySelector('.guide-page')?.dataset.guideTitle,
        language: language || 'en',
        type,
        ...params,
    };

    // GA4
    dataLayer.push({
        event: 'guide',
        guide_name: values.guide_name,
        label: values.label,
        language_choice: values.language,
        type: values.type,
        value: values.value,
    });
}

// Open stop overlay
export function stopOverlayOpen() {
    const overlay = document.querySelector('.guide__secondary');
    if (!overlay) return;

    overlay.scrollTop = 0;
    overlay.classList.add('active');
    document.querySelector('.audio-player')?.classList.add('audio-player--expanded');
    if (window.isNavSmall) {
        // Focus the back button so keyboard focus lands in the scrollable overlay
        setFocus('.guide-stop:not([hidden]) .guide-stop-controls__close');
    }
}

// Close small overlay
export function stopOverlayClose() {
    const overlay = document.querySelector('.guide__secondary');
    if (!overlay) return;

    pauseYT();
    overlay.classList.remove('active');
    document.querySelector('.audio-player')?.classList.remove('audio-player--expanded');
}

// Update the URL without a Turbo visit; guide state is fully described by the params
function setSearchParams(updates, { replace = false } = {}) {
    const url = new URL(window.location.href);
    url.search = new URLSearchParams({ ...queryParameters(), ...updates }).toString();

    if (replace) {
        // Preserve state (e.g. Turbo's restoration identifier) when replacing in place
        window.history.replaceState(window.history.state, '', url);
    } else {
        window.history.pushState(null, '', url);
    }
}

// Load the stop item's audio into the shared player, pausing whatever was playing
function updateAudioPlayer(page, stopEl, itemEl, shouldPlay) {
    const audioWrapper = document.getElementById('guide-audio-player');
    if (!audioWrapper) return;

    const audioPlay = audioWrapper.querySelector('.audio-player__play');
    const audioProgress = audioWrapper.querySelector('.audio-player__progress');
    const audioInfoText = audioWrapper.querySelector('.audio-player__info-text');
    const audio = audioWrapper.querySelector('audio');
    const cleanTitle = stopEl.dataset.title;
    // audio.src reads back absolute, so normalize for comparison
    const fileUrl = itemEl.dataset.fileUrl &&
        new URL(itemEl.dataset.fileUrl, window.location.origin).href;

    // Pause any playing audio and update content unless the selected audio is already loaded
    if (audio && fileUrl && (
        !audioWrapper.classList.contains('active') ||
        (audio.src !== fileUrl))) {
        audio.pause();
        // Add metadata into audio element for pickup later
        audio.dataset.label = cleanTitle;
        audio.dataset.artist = page.dataset.guideTitle;
        if (stopEl.dataset.coverImage) {
            audio.dataset.image = stopEl.dataset.coverImage;
        }
        audio.setAttribute('title', cleanTitle);

        audioPlay.classList.remove('playing');
        audioPlay.classList.add('paused');

        // Set visible audio title
        let text = `<strong>${ stopEl.dataset.titleHtml }</strong>`;
        if (stopEl.dataset.stopNumber) {
            text = `<strong><span class="audio-player__info-number">${ stopEl.dataset.stopNumber }—</span>${ stopEl.dataset.titleHtml }</strong>`;
        }
        audioInfoText.innerHTML = text;

        // Handle loading in content
        if (itemEl.dataset.mediaType === 'Audio') {
            audioProgress.style.width = '0px';
            audio.src = fileUrl;
            audio.load();

            if (shouldPlay) {
                audioPlay.classList.add('loading');
                audio.play().then(() => {
                    audioPlay.classList.add('playing');
                    audioPlay.classList.remove('paused', 'loading');
                }).catch(() => {
                    audioPlay.classList.remove('playing', 'loading');
                    audioPlay.classList.add('paused');
                });
            }

            audioWrapper.classList.add('active');
        }
    } else if (fileUrl) {
        audioWrapper.classList.add('active');
    } else {
        // If there's no audio, hide and pause the player
        audio.pause();
        audioWrapper.classList.remove('active');
    }
}

// Sync the DOM to the URL params: type filter, active stop/item, overlay and audio
function applyState(page, { shouldPlay = false } = {}) {
    const params = queryParameters();
    const type = getType(params);

    // Type filter buttons
    page.querySelectorAll('[data-guide-type]').forEach((button) => {
        button.classList.toggle('active', button.dataset.guideType === type);
    });

    // Show only playlist items whose server-computed data-filter-types include the type
    page.querySelectorAll('.playlist-item-wrapper').forEach((wrapper) => {
        wrapper.hidden = !(wrapper.dataset.filterTypes || '').split(' ').includes(type);
    });

    // The selected stop must be within the filtered playlist
    const activeWrapper = params.stop &&
        page.querySelector(`.playlist-item-wrapper:not([hidden]) .playlist-item[data-stop-id="${ params.stop }"]`);
    const activeStopEl = activeWrapper ? page.querySelector(`.guide-stop[data-stop-id="${ params.stop }"]`) : null;

    page.querySelectorAll('.playlist-item').forEach((link) => {
        link.classList.toggle('active', Boolean(activeStopEl) && link.dataset.stopId === params.stop);
    });
    page.querySelectorAll('.guide-stop').forEach((stopEl) => {
        stopEl.hidden = stopEl !== activeStopEl;
    });

    if (activeStopEl) {
        // Show the requested stop item, else the first matching the type filter, else the first
        const items = activeStopEl.querySelectorAll('.guide-stop-item');
        const itemEl = (params.stop_item &&
            activeStopEl.querySelector(`.guide-stop-item[data-stop-item-id="${ params.stop_item }"]`)) ||
            activeStopEl.querySelector(`.guide-stop-item[data-filter-types~="${ type }"]`) ||
            items[0];

        items.forEach((el) => {
            el.hidden = el !== itemEl;
        });
        activeStopEl.querySelectorAll('.link-rail-item').forEach((tab) => {
            tab.classList.toggle('active', tab.dataset.stopItemId === itemEl?.dataset.stopItemId);
        });

        if (itemEl && itemEl.dataset.stopItemId !== lastAppliedItemId) {
            lastAppliedItemId = itemEl.dataset.stopItemId;

            updateAudioPlayer(page, activeStopEl, itemEl, shouldPlay);

            // Move focus to the player for keyboard users when the selected stop changes
            if (params.open === 'true' && !window.isNavSmall) {
                setFocus('.audio-player__play');
            }

            activeStopEl.scrollTop = 0;

            // Reset link rail scroll when switching stops
            if (!params.stop_item) {
                activeStopEl.querySelectorAll('.link-rail__list').forEach((list) => {
                    list.scrollLeft = 0;
                });
            }

            adjustLinkRails();
            setupScrollGradients();
            setupVideos();

            // Save the location on the player so clicking it elsewhere navigates back here
            const audioWrapper = document.getElementById('guide-audio-player');
            if (audioWrapper) {
                audioWrapper.dataset.activeItem = JSON.stringify({
                    guideUrl: page.dataset.guideUrl,
                    params,
                });
            }

            // Report the stop item (and the stop when it changed too)
            let analyticsTitle = activeStopEl.dataset.title;
            if (activeStopEl.dataset.stopNumber) {
                analyticsTitle += ` (${ activeStopEl.dataset.stopNumber })`;
            }
            pushGuideEvent({
                label: 'stop item',
                value: analyticsTitle,
                type: itemEl.dataset.itemType,
                language: itemEl.dataset.itemLanguage,
            });
            if (activeStopEl.dataset.stopId !== lastTrackedStopId) {
                lastTrackedStopId = activeStopEl.dataset.stopId;
                pushGuideEvent({ label: 'stop', value: analyticsTitle, language: itemEl.dataset.itemLanguage });
            }
        }
    }

    // Open/close the overlay to follow the `open` param
    if (params.open === 'true' && activeStopEl) {
        stopOverlayOpen();
    } else {
        stopOverlayClose();
    }
}

function handleStopChange(page, selectedStopId) {
    const params = { ...queryParameters(), stop: selectedStopId, open: 'true' };
    delete params.stop_item;
    setSearchParams(params);

    applyState(page, { shouldPlay: true });
}

function handleItemChange(page, selectedItemId) {
    setSearchParams({ ...queryParameters(), stop_item: selectedItemId });

    applyState(page, { shouldPlay: true });
}

function handleTypeChange(page, type) {
    // Replace so the Back button doesn't step through filter changes
    setSearchParams({ ...queryParameters(), type }, { replace: true });

    applyState(page);

    // Set focus on the first stop in the list
    setFocus('.playlist-item-wrapper:not([hidden]) .playlist-item');

    pushGuideEvent({ label: 'select', value: type, type });
}

function handleStopClose() {
    stopOverlayClose();

    setSearchParams({ ...queryParameters(), open: 'false' }, { replace: true });

    setFocus('.playlist-item.active');
    pushGuideEvent({ label: 'click', type: '', value: 'close stop' });
}

// Clicking the persistent player away from the active stop navigates back to it
function bindAudioReturnClick() {
    const audioWrapper = document.getElementById('guide-audio-player');
    // The player is turbo-permanent, so only bind once
    if (!audioWrapper || audioWrapper.dataset.returnBound) return;
    audioWrapper.dataset.returnBound = 'true';

    audioWrapper.addEventListener('click', (e) => {
        // Only open if it wasn't a tap on a player control
        if (e.target.closest('button') ||
            e.target.classList.contains('audio-player__progress') ||
            e.target.classList.contains('audio-player__timeline')) {
            return;
        }

        // Get the location saved when the audio was loaded and navigate there
        const { guideUrl, params } = safelyParseJSON(audioWrapper.dataset.activeItem) || {};
        if (!guideUrl || !params) return;

        const activeItemParams = { ...params };
        if (window.isNavSmall) activeItemParams.open = 'true';
        const activeItemUrl = `${ guideUrl }?${ new URLSearchParams(activeItemParams) }`;

        // Don't navigate if you're already on the stop
        if (window.location.pathname === guideUrl && queryParameters().stop === params.stop) return;

        window.Turbo.visit(activeItemUrl);
    });
}

function bindGlobalEvents() {
    if (globalEventsBound) return;
    globalEventsBound = true;

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && guideShowPage()) handleStopClose();
    });

    // Back/forward: reapply from the URL, or let Turbo re-render if the body doesn't match it
    window.addEventListener('popstate', (event) => {
        if (event.state?.turbo) return; // Turbo restores its own entries

        if (!GUIDE_PATH_PATTERN.test(window.location.pathname)) return;

        const page = guideShowPage();
        if (page && window.location.pathname === page.dataset.guideUrl) {
            applyState(page, { shouldPlay: false });
        } else {
            window.Turbo.visit(window.location.href, { action: 'restore' });
        }
    });
}

// One delegated listener per event type; branches without matching elements never fire
function bindPage(page) {
    page.addEventListener('click', (e) => {
        const stopLink = e.target.closest('.playlist-item');
        if (stopLink) {
            e.preventDefault();
            handleStopChange(page, stopLink.dataset.stopId);
            return;
        }

        const tabLink = e.target.closest('.link-rail-item__link');
        if (tabLink) {
            e.preventDefault();
            handleItemChange(page, tabLink.closest('.link-rail-item').dataset.stopItemId);
            return;
        }

        const typeButton = e.target.closest('[data-guide-type]');
        if (typeButton && !typeButton.disabled) {
            handleTypeChange(page, typeButton.dataset.guideType);
            return;
        }

        if (e.target.closest('.guide-stop-controls__close')) {
            handleStopClose();
            return;
        }

        // Pagination analytics (the links themselves are regular Turbo visits)
        const pageLink = e.target.closest('.pagination a[data-page]');
        if (pageLink) {
            pushGuideEvent({ label: 'page', value: pageLink.dataset.page });
        }
    });

    page.addEventListener('change', (e) => {
        // Language is part of the server render, so switching is a real visit
        if (e.target.matches('.guide-language-select')) {
            const language = e.target.value;
            pushGuideEvent({ label: 'language', language, value: language });

            const url = new URL(window.location.href);
            url.searchParams.set('language', language);
            window.Turbo.visit(url.toString());
            return;
        }

    });
}

export function setupGuides() {
    const page = document.querySelector('.guide-page');
    if (!page) return;

    bindAudioReturnClick();
    bindGlobalEvents();

    // Frame renders (e.g. flash messages) re-run setup but the page element survives; bind once
    if (page.dataset.guidesBound) return;
    page.dataset.guidesBound = 'true';

    bindPage(page);

    if (page.hasAttribute('data-guide-list')) {
        // Google Analytics page load event
        pushGuideEvent({ label: 'overview', value: 'load' });
        return;
    }

    // Fresh guide view: re-report and re-apply as if newly mounted
    lastAppliedItemId = null;
    lastTrackedStopId = null;

    // Google Analytics page load event
    pushGuideEvent({ label: 'guide', value: page.dataset.guideTitle });

    applyState(page, { shouldPlay: false });
}
